import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, Phone, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthProvider';
import { postLoginPath } from '../lib/roles';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { PasswordField } from '../components/auth/PasswordField';
import { Illustration } from '../components/common/Illustration';
import { completePhoneSignIn, formatFirebasePhoneAuthError, startPhoneSignIn } from '../lib/firebase/phoneAuth';

export function LoginScreen() {
  const { loginWithPhone, login, status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  // Phone OTP login states (Primary)
  const [mobile, setMobile] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpConfirmation, setOtpConfirmation] = useState<
    | { mode: 'web'; confirmation: import('firebase/auth').ConfirmationResult }
    | { mode: 'native'; verificationId: string }
    | null
  >(null);
  const [otpLoading, setOtpLoading] = useState(false);

  // Email login states (Secondary fallback)
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Send OTP handler
  const handleSendOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setOtpLoading(true);
    try {
      const result = await startPhoneSignIn(mobile);
      if (result.mode === 'native' && result.verificationId === '__AUTO__') {
        const idToken = await completePhoneSignIn(result, '');
        const me = await loginWithPhone(idToken);
        navigate(postLoginPath(me.role, from), { replace: true });
        return;
      }
      setOtpConfirmation(result);
      setSuccess(`Verification code sent to ${mobile.trim().length === 10 ? '+91' + mobile.trim() : mobile.trim()}`);
    } catch (err: unknown) {
      console.error('Error sending OTP:', err);
      setError(formatFirebasePhoneAuthError(err));
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP & Login handler
  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!otpConfirmation) return;
    setError(null);
    setSuccess(null);
    setOtpLoading(true);
    try {
      const idToken = await completePhoneSignIn(otpConfirmation, otpCode);
      const me = await loginWithPhone(idToken);
      navigate(postLoginPath(me.role, from), { replace: true });
    } catch (err: any) {
      console.error('Error verifying OTP:', err);
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Fallback Email Login handler
  const onSubmitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setOtpLoading(true);
    try {
      const me = await login(email, password);
      navigate(postLoginPath(me.role, from), { replace: true });
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Already signed in — go straight to the dashboard.
  if (status === 'authenticated' && user) {
    return <Navigate to={postLoginPath(user.role, from)} replace />;
  }

  return (
    <section className="adaptive-page auth-screen">
      <div className="auth-card glass-surface">
        <Illustration context="page.auth" size="sm" style={{ margin: '0 auto 16px auto', display: 'block', maxHeight: '130px' }} />
        <span className="section-kicker">Welcome back</span>
        <h1>Log in to WebFoxx</h1>
        <p>Access your dashboard, mock tests, and revision tools.</p>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="auth-success" role="alert" style={{ background: 'rgba(46, 184, 46, 0.1)', color: '#2eb82e', padding: '10px 14px', borderRadius: '6px', fontSize: '0.9rem', marginBottom: '16px', border: '1px solid rgba(46, 184, 46, 0.2)' }}>
            {success}
          </div>
        )}

        {!showEmailLogin ? (
          /* Primary Mobile OTP Sign-in Form */
          !otpConfirmation ? (
            <form className="auth-form" onSubmit={handleSendOtp}>
              <label className="auth-field">
                <Phone size={17} aria-hidden="true" />
                <span className="sr-only">Mobile Number</span>
                <input
                  type="tel"
                  required
                  placeholder="Mobile Number (e.g. 9999999999)"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ''))}
                />
              </label>
              <button type="submit" className="primary-action" disabled={otpLoading}>
                {otpLoading ? 'Sending OTP…' : 'Send Verification OTP'}
                {!otpLoading && <ArrowRight size={18} aria-hidden="true" />}
              </button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleVerifyOtp}>
              <label className="auth-field">
                <Lock size={17} aria-hidden="true" />
                <span className="sr-only">Verification Code</span>
                <input
                  type="text"
                  maxLength={6}
                  required
                  placeholder="6-Digit OTP Code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  style={{ letterSpacing: otpCode ? '4px' : 'normal', textAlign: otpCode ? 'center' : 'left' }}
                />
              </label>
              <button type="submit" className="primary-action" disabled={otpLoading || otpCode.length !== 6}>
                {otpLoading ? 'Signing in…' : 'Verify & Log In'}
                {!otpLoading && <ArrowRight size={18} aria-hidden="true" />}
              </button>
              <button
                type="button"
                className="secondary-action"
                onClick={() => {
                  setOtpConfirmation(null);
                  setOtpCode('');
                  setError(null);
                  setSuccess(null);
                }}
                style={{ marginTop: '8px', width: '100%', display: 'flex', justifyContent: 'center' }}
              >
                Change Number
              </button>
            </form>
          )
        ) : (
          /* Secondary Fallback Email Sign-in Form */
          <form className="auth-form" onSubmit={onSubmitEmail}>
            <label className="auth-field">
              <Mail size={17} aria-hidden="true" />
              <span className="sr-only">Email</span>
              <input
                type="email"
                required
                placeholder="you@example.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <PasswordField
              label="Password"
              placeholder="Your password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
            />
            <button type="submit" className="primary-action" disabled={otpLoading}>
              {otpLoading ? 'Signing in…' : 'Log in'}
              {!otpLoading && <ArrowRight size={18} aria-hidden="true" />}
            </button>
          </form>
        )}

        <div style={{ margin: '16px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            type="button"
            className="secondary-action"
            onClick={() => {
              setShowEmailLogin(!showEmailLogin);
              setError(null);
              setSuccess(null);
            }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center', fontSize: '0.85rem' }}
          >
            {showEmailLogin ? 'Sign in with Mobile Number (OTP)' : 'Sign in with Password instead'}
          </button>
        </div>

        <GoogleSignInButton
          onSignedIn={(me) => navigate(postLoginPath(me.role, from), { replace: true })}
        />

        <div className="auth-links">
          {showEmailLogin && <Link to="/forgot-password">Forgot password?</Link>}
          <span>
            New here? <Link to="/register">Create an account</Link>
          </span>
        </div>
      </div>

      {/* reCAPTCHA Anchor Container */}
      <div id="recaptcha-container"></div>
    </section>
  );
}
